import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import type {
	ButtonHTMLAttributes,
	InputHTMLAttributes,
	LabelHTMLAttributes,
	ReactNode,
	SelectHTMLAttributes,
	TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

/* ─── Button ──────────────────────────────────────────────────────── */

const buttonVariants = cva(
	"inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-sm font-mono text-[11px] font-medium uppercase tracking-[0.12em] transition-all duration-150 disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-3.5 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				primary:
					"bg-primary text-primary-foreground shadow-[inset_0_1px_0_0_oklch(1_0_0/0.18),inset_0_-1.5px_0_0_oklch(0_0_0/0.22)] hover:brightness-110 active:brightness-95",
				secondary:
					"border border-border bg-transparent text-foreground/80 hover:border-input hover:bg-accent hover:text-foreground",
				ghost: "text-muted-foreground hover:bg-accent hover:text-foreground",
				danger:
					"border border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20",
			},
			size: {
				sm: "h-8 px-3",
				md: "h-9 px-4",
				lg: "h-10 px-5",
				icon: "size-8",
			},
		},
		defaultVariants: { variant: "secondary", size: "sm" },
	},
);

export interface ButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {}

export function Button({ className, variant, size, type = "button", ...props }: ButtonProps) {
	return (
		<button type={type} className={cn(buttonVariants({ variant, size }), className)} {...props} />
	);
}

export { buttonVariants };

/* ─── Field chrome ────────────────────────────────────────────────── */

export function Field({
	label,
	hint,
	className,
	children,
	...props
}: LabelHTMLAttributes<HTMLLabelElement> & { label: ReactNode; hint?: string }) {
	return (
		<>
			{/* flex+gap instead of space-y: components that render out-of-flow hidden inputs
			    (e.g. Base UI comboboxes) would otherwise count as the label's last child and
			    push trailing margin onto the visible control. */}
			{/* biome-ignore lint/a11y/noLabelWithoutControl: children always render the control this label wraps */}
			<label className={cn("flex flex-col gap-1.5", className)} {...props}>
				<span className="label-micro block text-muted-foreground">{label}</span>
				{children}
				{hint ? <p className="text-xs text-muted-foreground/80">{hint}</p> : null}
			</label>
		</>
	);
}

export const inputChrome =
	"w-full rounded-sm border border-input bg-transparent px-3 text-sm text-foreground transition-colors placeholder:text-muted-foreground/60 hover:border-muted-foreground/40 focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
	return <input className={cn(inputChrome, "h-9", className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
	return <textarea className={cn(inputChrome, "py-2", className)} {...props} />;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
	wrapperClassName?: string;
}

export function Select({ className, wrapperClassName, children, ...props }: SelectProps) {
	return (
		<div className={cn("relative", wrapperClassName)}>
			<select
				className={cn(inputChrome, "h-9 appearance-none pr-8 font-medium", className)}
				{...props}
			>
				{children}
			</select>
			<ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
		</div>
	);
}

/* ─── Badge: stamped status plate ────────────────────────────────── */

const badgeVariants = cva(
	"inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.1em]",
	{
		variants: {
			variant: {
				default: "border-border bg-muted/50 text-muted-foreground",
				ember: "border-primary/30 bg-primary/10 text-primary",
				patina: "border-success/30 bg-success/10 text-success",
				danger: "border-destructive/30 bg-destructive/10 text-destructive",
				outline: "border-border text-muted-foreground",
			},
		},
		defaultVariants: { variant: "default" },
	},
);

export interface BadgeProps
	extends React.HTMLAttributes<HTMLSpanElement>,
		VariantProps<typeof badgeVariants> {
	dot?: boolean;
}

export function Badge({ className, variant, dot = false, children, ...props }: BadgeProps) {
	return (
		<span className={cn(badgeVariants({ variant }), className)} {...props}>
			{dot ? <span className="status-dot size-1.5" /> : null}
			{children}
		</span>
	);
}

/* ─── Inline code: stamped slug ──────────────────────────────────── */

export function DataCode({ className, children }: { className?: string; children: ReactNode }) {
	return (
		<code
			className={cn(
				"rounded-sm border border-border/70 bg-muted/60 px-1.5 py-0.5 font-mono text-xs text-foreground/85",
				className,
			)}
		>
			{children}
		</code>
	);
}
